import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Sparkles, Eye, Brain, Lock, Loader2, BookOpen, Wand2,
  Eye as EyeIcon, Dices, Compass, Box, Star, Zap, ChevronLeft,
  Hand, Stars, ScrollText, Gamepad2, Shuffle,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  DailyCardGame, MemoryGame, OracleGame, WheelGame, TriviaGame,
} from "@/games/existing";
import {
  PendulumGame, RuneCastGame, TreasureChestGame, DiceGame,
  ReflexGame, SimonGame, GuessZodiacGame, GuessTarotGame,
} from "@/games/MoreGames";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* -------------------------------------------------------------------------- */
/* GAME REGISTRY                                                              */
/* -------------------------------------------------------------------------- */
const GROUPS = [
  {
    id: "adivinacion",
    label: "Adivinación",
    icon: EyeIcon,
    color: "from-purple-700 to-pink-700",
    description: "Cartas, runas, péndulo, ruleta y otros oráculos.",
    games: [
      { id: "daily-card", label: "Carta del Día",        icon: Sparkles, render: (auth) => <DailyCardGame authHeader={auth} />, hint: "1×día" },
      { id: "oracle",     label: "Oráculo Sí/No",        icon: Eye,      render: (auth) => <OracleGame authHeader={auth} />,    hint: "cuota diaria" },
      { id: "wheel",      label: "Ruleta de la Fortuna", icon: Wand2,    render: (auth) => <WheelGame authHeader={auth} />,     hint: "1×día" },
      { id: "pendulum",   label: "Péndulo Espiritual",   icon: Hand,     render: () => <PendulumGame /> },
      { id: "runes",      label: "Tirada de Runas",      icon: Compass,  render: () => <RuneCastGame /> },
      { id: "chest",      label: "Cofre del Destino",    icon: Box,      render: () => <TreasureChestGame /> },
      { id: "dice",       label: "Dados del Universo",   icon: Dices,    render: () => <DiceGame /> },
    ],
  },
  {
    id: "habilidad",
    label: "Habilidad",
    icon: Brain,
    color: "from-cyan-700 to-blue-700",
    description: "Reflejos, memoria y concentración mística.",
    games: [
      { id: "memory", label: "Memoria Mística",   icon: Brain, render: (auth) => <MemoryGame authHeader={auth} /> },
      { id: "reflex", label: "Reflejos Místicos", icon: Zap,   render: () => <ReflexGame /> },
      { id: "simon",  label: "Secuencia Mística", icon: Star,  render: () => <SimonGame /> },
    ],
  },
  {
    id: "conocimiento",
    label: "Conocimiento",
    icon: BookOpen,
    color: "from-amber-700 to-orange-700",
    description: "Pon a prueba tu sabiduría espiritual.",
    games: [
      { id: "trivia",        label: "Trivia Astrológica",   icon: BookOpen,   render: (auth) => <TriviaGame authHeader={auth} /> },
      { id: "guess-zodiac",  label: "Adivina el Signo",     icon: Stars,      render: () => <GuessZodiacGame /> },
      { id: "guess-tarot",   label: "Adivina la Carta",     icon: ScrollText, render: () => <GuessTarotGame /> },
    ],
  },
];

const ALL_GAMES = GROUPS.flatMap((g) => g.games.map((game) => ({ ...game, groupId: g.id })));

/* -------------------------------------------------------------------------- */
/* PAGE                                                                       */
/* -------------------------------------------------------------------------- */
export default function GamesPage() {
  const { authHeader, isUser } = useAuth();
  const [enabled, setEnabled] = useState(true);
  const [checking, setChecking] = useState(true);
  const [activeGroup, setActiveGroup] = useState(GROUPS[0].id);
  const [activeGame, setActiveGame] = useState(null); // { groupId, id }

  useEffect(() => {
    if (!isUser) return;
    axios.get(`${API}/games/status`, { headers: authHeader() })
      .then((r) => setEnabled(!!r.data?.enabled))
      .catch(() => setEnabled(false))
      .finally(() => setChecking(false));
  }, [isUser, authHeader]);

  const surpriseMe = () => {
    const pick = ALL_GAMES[Math.floor(Math.random() * ALL_GAMES.length)];
    setActiveGroup(pick.groupId);
    setActiveGame({ groupId: pick.groupId, id: pick.id });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const currentGroup = useMemo(
    () => GROUPS.find((g) => g.id === activeGroup) || GROUPS[0],
    [activeGroup],
  );

  const currentGame = useMemo(() => {
    if (!activeGame) return null;
    return ALL_GAMES.find((g) => g.id === activeGame.id) || null;
  }, [activeGame]);

  if (!isUser) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 text-center pt-24">
        <div>
          <Sparkles className="w-12 h-12 text-[#d4af37] mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-white mb-2">Entretenimiento Místico</h1>
          <p className="text-sm text-neutral-400">Necesitas iniciar sesión para jugar.</p>
        </div>
      </div>
    );
  }

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" /></div>;
  }

  if (!enabled) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 text-center pt-24" data-testid="games-blocked">
        <div className="max-w-md">
          <Lock className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-white mb-2">Acceso a juegos bloqueado</h1>
          <p className="text-sm text-neutral-400">El administrador bloqueó tu acceso a esta sección. Si crees que es un error, contáctalo.</p>
        </div>
      </div>
    );
  }

  /* ------------------------------ ACTIVE GAME VIEW -------------------------- */
  if (currentGame) {
    return (
      <div className="min-h-screen pt-24 pb-24 px-3 sm:px-6 max-w-3xl mx-auto" data-testid="games-page">
        <button
          type="button"
          onClick={() => setActiveGame(null)}
          data-testid="games-back"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-neutral-300 hover:text-[#d4af37] mb-6"
        >
          <ChevronLeft className="w-4 h-4" /> Volver a juegos
        </button>
        <div className="glass-dark rounded-2xl border border-white/10 p-4 sm:p-6">
          {currentGame.render(authHeader)}
        </div>
      </div>
    );
  }

  /* ------------------------------ GAMES INDEX ------------------------------- */
  return (
    <div className="min-h-screen pt-24 pb-24 px-3 sm:px-6 max-w-5xl mx-auto" data-testid="games-page">
      <div className="text-center mb-8">
        <Sparkles className="w-10 h-10 text-[#d4af37] mx-auto mb-2" />
        <h1 className="font-serif text-3xl sm:text-4xl text-white">Entretenimiento Místico</h1>
        <p className="text-sm text-neutral-400 mt-2 max-w-lg mx-auto">
          {ALL_GAMES.length} juegos místicos para acompañarte. Elige un grupo o deja que el destino decida.
        </p>
        <button
          type="button"
          onClick={surpriseMe}
          data-testid="games-surprise"
          className="btn-gold mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs uppercase tracking-widest"
        >
          <Shuffle className="w-4 h-4" /> Sorpréndeme
        </button>
      </div>

      {/* Group tabs */}
      <div className="flex flex-wrap items-center justify-center gap-2 mb-8">
        {GROUPS.map((g) => {
          const Icon = g.icon;
          const active = g.id === activeGroup;
          return (
            <button
              key={g.id}
              type="button"
              onClick={() => setActiveGroup(g.id)}
              data-testid={`games-group-${g.id}`}
              className={`group inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs uppercase tracking-widest transition-all ${
                active
                  ? "bg-[#d4af37] text-black"
                  : "bg-black/40 border border-white/10 text-neutral-300 hover:text-white hover:border-[#d4af37]/40"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {g.label}
              <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${active ? "bg-black/15 text-black" : "bg-white/5 text-neutral-400"}`}>
                {g.games.length}
              </span>
            </button>
          );
        })}
      </div>

      <p className="text-center text-xs text-neutral-400 mb-5 italic">
        {currentGroup.description}
      </p>

      {/* Game cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4" data-testid={`games-group-content-${activeGroup}`}>
        {currentGroup.games.map((game) => {
          const Icon = game.icon;
          return (
            <motion.button
              key={game.id}
              type="button"
              whileHover={{ y: -4, scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setActiveGame({ groupId: currentGroup.id, id: game.id })}
              data-testid={`games-card-${game.id}`}
              className={`relative rounded-2xl border border-white/10 hover:border-[#d4af37]/50 bg-gradient-to-br ${currentGroup.color} p-4 sm:p-5 text-left overflow-hidden group transition-all`}
            >
              <div className="absolute inset-0 bg-black/55 group-hover:bg-black/40 transition-colors" />
              <div className="relative z-10">
                <div className="w-10 h-10 rounded-xl bg-[#d4af37]/15 border border-[#d4af37]/40 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-[#d4af37]" />
                </div>
                <div className="text-white font-serif text-sm sm:text-base leading-tight">{game.label}</div>
                {game.hint && (
                  <div className="text-[10px] text-[#d4af37]/80 uppercase tracking-widest mt-1">{game.hint}</div>
                )}
                <div className="text-[10px] text-neutral-300/80 mt-2 inline-flex items-center gap-1">
                  <Gamepad2 className="w-3 h-3" /> Jugar
                </div>
              </div>
              <Sparkles className="absolute -right-2 -bottom-2 w-16 h-16 text-[#d4af37]/10 group-hover:text-[#d4af37]/20 transition-colors" />
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
